import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlass, Scissors } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, photoHref } from "@/lib/api";

const live = (list) => (list || []).filter((p) => p && !p.is_deleted && (p.storage_path || p.thumb_path));

// Flatten slab + child-piece photos and remnant photos into one pickable list.
export const inventoryPhotoItems = (slabs, remnants, material) => {
  const mat = (material || "").toLowerCase();
  const out = [];
  (slabs || []).forEach((s) => {
    const push = (p, serial) =>
      out.push({
        key: `s-${p.storage_path || p.thumb_path}`,
        path: p.storage_path || p.thumb_path,
        thumb: p.thumb_path || p.storage_path,
        color: s.slab_color || "",
        code: s.code || "",
        serial: serial || "",
        kind: "slab",
        length_in: Number(s.length_in) || 0,
        width_in: Number(s.width_in) || 0,
        match: (s.slab_color || "").toLowerCase() === mat,
      });
    live(s.photos).forEach((p) => push(p, s.serial_number));
    (s.pieces || []).forEach((pc) => live(pc.photos).forEach((p) => push(p, pc.serial_number)));
  });
  (remnants || []).forEach((r) => {
    live(r.photos)
      .sort((a, b) => Number(!!b.shape) - Number(!!a.shape))
      .forEach((p) =>
        out.push({
          key: `r-${p.storage_path || p.thumb_path}`,
          path: p.storage_path || p.thumb_path,
          thumb: p.thumb_path || p.storage_path,
          color: r.slab_color || "",
          code: r.code || "",
          serial: r.source_tag || "",
          kind: "remnant",
          shape: !!p.shape,
          length_in: Number(r.length_in) || 0,
          width_in: Number(r.width_in) || 0,
          match: (r.slab_color || "").toLowerCase() === mat,
        }),
      );
  });
  return out.sort((a, b) => Number(b.match) - Number(a.match));
};

/** Big picker for a slab-sheet photo: every inventory slab photo plus remnant photos
 *  (shape-cropped pieces first), this material on top, with search and a kind filter. */
export const InventoryPhotoDialog = ({ open, onClose, inventorySlabs = [], material = "", onPick }) => {
  const [remnants, setRemnants] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [resize, setResize] = useState(true);

  useEffect(() => {
    if (!open || remnants) return;
    api
      .get("/remnants")
      .then(({ data }) => setRemnants(Array.isArray(data) ? data : []))
      .catch(() => setRemnants([]));
  }, [open, remnants]);

  const items = useMemo(
    () => inventoryPhotoItems(inventorySlabs, remnants || [], material),
    [inventorySlabs, remnants, material],
  );
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter(
      (it) =>
        (kind === "all" || it.kind === kind) &&
        (!needle || [it.color, it.code, it.serial].join(" ").toLowerCase().includes(needle)),
    );
  }, [items, q, kind]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-h-[92vh] overflow-hidden border-zinc-800 bg-zinc-950 p-0 sm:max-w-5xl"
        data-testid="inventory-photo-dialog"
      >
        <DialogHeader className="border-b border-zinc-800 px-5 pt-5 pb-3">
          <DialogTitle className="text-zinc-100">Slab photo from inventory</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Pick a real slab or remnant photo for this sheet. {material ? `${material} is listed first.` : ""}
          </DialogDescription>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
              <MagnifyingGlass size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                autoFocus
                data-testid="inventory-photo-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search color, code or tag…"
                className="h-10 border-zinc-800 bg-zinc-900 pl-9 text-sm"
              />
            </div>
            <div className="flex overflow-hidden rounded-md border border-zinc-700" data-testid="inventory-photo-kind">
              {[
                ["all", "All"],
                ["slab", "Slabs"],
                ["remnant", "Remnants"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`inventory-photo-kind-${k}`}
                  onClick={() => setKind(k)}
                  className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    kind === k ? "bg-indigo-500 text-white" : "text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-[11px] text-zinc-300">
              <input
                type="checkbox"
                data-testid="inventory-photo-resize"
                checked={resize}
                onChange={(e) => setResize(e.target.checked)}
                className="h-4 w-4 accent-indigo-400"
              />
              Resize sheet to the slab's size
            </label>
          </div>
        </DialogHeader>
        <div className="max-h-[calc(92vh-170px)] overflow-y-auto p-5" data-testid="inventory-photo-grid">
          {remnants === null && (
            <p className="mb-3 text-[11px] text-zinc-500" data-testid="inventory-photo-loading">
              Loading remnant photos…
            </p>
          )}
          {shown.length === 0 ? (
            <p className="py-10 text-center text-sm text-zinc-500" data-testid="inventory-photo-empty">
              No photos match. Add photos to a slab or remnant first.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {shown.map((it, idx) => (
                <button
                  key={it.key}
                  type="button"
                  data-testid={`inventory-photo-${idx}`}
                  onClick={() => onPick(it, resize)}
                  title={[it.color, it.code, it.serial].filter(Boolean).join(" · ")}
                  className={`group relative aspect-[4/3] overflow-hidden rounded-lg border text-left transition-all duration-150 hover:-translate-y-0.5 hover:border-indigo-400 hover:shadow-[0_12px_30px_-12px_rgba(99,102,241,0.7)] ${
                    it.match ? "border-zinc-700" : "border-zinc-800 opacity-75 hover:opacity-100"
                  } ${it.shape ? "bg-[repeating-conic-gradient(#27272a_0_25%,#18181b_0_50%)] bg-[length:16px_16px]" : "bg-zinc-900"}`}
                >
                  <img
                    src={photoHref(it.thumb)}
                    alt={it.color}
                    loading="lazy"
                    className={`h-full w-full ${it.shape ? "object-contain p-1" : "object-cover"}`}
                  />
                  <span
                    className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                      it.kind === "remnant" ? "bg-amber-400 text-zinc-950" : "bg-zinc-950/80 text-zinc-200"
                    }`}
                  >
                    {it.kind === "remnant" ? (it.shape ? "Remnant · shape" : "Remnant") : "Slab"}
                  </span>
                  {it.shape && (
                    <Scissors size={14} weight="bold" className="absolute right-2 top-2 text-amber-300" />
                  )}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-zinc-950/95 to-zinc-950/10 px-2.5 pb-2 pt-5">
                    <span className="block truncate text-xs font-bold text-zinc-50">
                      {it.serial || it.code || it.color}
                    </span>
                    <span className="block truncate text-[10px] text-zinc-300">
                      {it.color}
                      {it.length_in && it.width_in ? ` · ${it.length_in}" × ${it.width_in}"` : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
